import type { BackendModule, i18n, ResourceKey } from "i18next";
import { initReactI18next } from "react-i18next";
import type { ResolvedLocale } from "./locale.js";

export type TranslationFailure = "translations_timeout" | "translations_unavailable";
export class TranslationLoadError extends Error {
  constructor(readonly code: TranslationFailure) {
    super(code);
    this.name = "TranslationLoadError";
  }
}

export const TRANSLATION_TIMEOUT_MS = 15_000;
const BOOT_NAMESPACES = ["common", "shell"] as const;
const REQUIRED_KEYS = {
  common: ["actions.cancel", "actions.retry"],
  shell: ["brand", "tabs.dashboard", "tabs.search", "nav.groups.now", "nav.groups.home"]
} as const;

export function hasUsableTranslations(runtime: i18n, locale: ResolvedLocale): boolean {
  if (!runtime.isInitialized || runtime.language !== locale) return false;
  return BOOT_NAMESPACES.every((namespace) => {
    const hasBundle = [locale, "en"].some((language) => {
      const bundle = runtime.getResourceBundle(language, namespace);
      return bundle && Object.keys(bundle).length > 0;
    });
    return hasBundle
      && REQUIRED_KEYS[namespace].every((key) => runtime.exists(key, {
        lng: locale, ns: namespace, returnEmptyString: false
      }));
  });
}

type BundleLoader = (language: string, namespace: string) => Promise<ResourceKey>;

/** One lifecycle owns init and language changes; callers must not race changeLanguage against init. */
export function createI18nLifecycle(
  runtime: i18n,
  fetchBundle: BundleLoader,
  timeoutMs = TRANSLATION_TIMEOUT_MS
) {
  const inFlight = new Map<string, Promise<ResourceKey>>();
  const failures = new Map<string, TranslationFailure>();
  let tail: Promise<unknown> = Promise.resolve();
  let latest: { locale: ResolvedLocale; promise: Promise<void> } | undefined;

  function loadBundle(language: string, namespace: string): Promise<ResourceKey> {
    const key = `${language}|${namespace}`;
    const existing = inFlight.get(key);
    if (existing) return existing;
    const pending = new Promise<ResourceKey>((resolve, reject) => {
      const timer = setTimeout(() => reject(new TranslationLoadError("translations_timeout")), timeoutMs);
      // Dynamic imports cannot be aborted. A late result cannot populate the runtime
      // after this attempt's deadline, and cleanup always removes its timer.
      Promise.resolve().then(() => fetchBundle(language, namespace)).then(resolve, () => {
        reject(new TranslationLoadError("translations_unavailable"));
      }).finally(() => clearTimeout(timer));
    }).then((bundle) => {
      failures.delete(key);
      return bundle;
    }, (error: TranslationLoadError) => {
      failures.set(key, error.code);
      throw error;
    }).finally(() => {
      if (inFlight.get(key) === pending) inFlight.delete(key);
    });
    inFlight.set(key, pending);
    return pending;
  }

  const backend: BackendModule = {
    type: "backend",
    init() { /* The bundle map is supplied by Vite. */ },
    read(language, namespace, callback) {
      void loadBundle(language, namespace).then(
        (bundle) => callback(null, bundle),
        (error: Error) => callback(error, false)
      );
    }
  };

  async function prepare(locale: ResolvedLocale): Promise<void> {
    if (!runtime.isInitialized) {
      await runtime.use(backend).use(initReactI18next).init({
        lng: locale,
        fallbackLng: "en",
        defaultNS: "common",
        ns: [...BOOT_NAMESPACES],
        interpolation: { escapeValue: false },
        parseMissingKeyHandler: (key) => key.split(":").pop() ?? key,
        react: { useSuspense: false }
      });
    } else {
      const languages = locale === "en" ? ["en"] : [locale, "en"];
      // i18next caches failed backend reads. reloadResources cannot retry its
      // negative states, so retry missing boot bundles using public resource APIs.
      await Promise.all(languages.flatMap((language) => BOOT_NAMESPACES.map(async (namespace) => {
        const existing = runtime.getResourceBundle(language, namespace);
        if (existing && REQUIRED_KEYS[namespace].every((key) => runtime.exists(key, {
          lng: language, ns: namespace, fallbackLng: false, returnEmptyString: false
        }))) return;
        try {
          const bundle = await loadBundle(language, namespace);
          runtime.addResourceBundle(language, namespace, bundle);
        } catch {
          // An available English bundle may still make this namespace usable.
        }
      })));
      if (runtime.language !== locale) await runtime.changeLanguage(locale);
    }
    if (!hasUsableTranslations(runtime, locale)) {
      const languages = locale === "en" ? ["en"] : [locale, "en"];
      const timedOut = languages.some((language) => BOOT_NAMESPACES.some(
        (namespace) => failures.get(`${language}|${namespace}`) === "translations_timeout"
      ));
      throw new TranslationLoadError(timedOut ? "translations_timeout" : "translations_unavailable");
    }
  }

  function ensure(locale: ResolvedLocale): Promise<void> {
    // StrictMode and concurrent consumers share a single attempt, but en -> es
    // -> en remains ordered: only adjacent requests for the same locale coalesce.
    if (latest?.locale === locale) return latest.promise;
    const promise = tail.then(() => prepare(locale));
    latest = { locale, promise };
    tail = promise.catch(() => undefined);
    void promise.finally(() => {
      if (latest?.promise === promise) latest = undefined;
    }).catch(() => undefined);
    return promise;
  }

  return { ensure };
}
